import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

import { API_URL } from '../utils/api';
import { logger } from '../utils/logger';
import { tryExtensionBootstrap } from '../utils/extensionSso';

// Global axios response interceptor.
// Strategy on 401:
//   1. Try to call POST /api/auth/refresh silently (uses the 7-day refresh_token cookie)
//   2. If refresh succeeds → replay the original request transparently
//   3. If refresh fails → dispatch `session:expired` event so the UI can show a banner
//
// This prevents the "session expired" banner from appearing every 4 hours (the access_token TTL)
// when the user's real session (refresh_token) is still valid for 7 days.
let _authInterceptorInstalled = false;
let _refreshPromise = null; // dedupe concurrent refreshes
let _lastRefreshAt = 0;     // iter 69 — cooldown to absorb late-arriving 401s

const installAuthInterceptor = (onUnauthorized) => {
  if (_authInterceptorInstalled) return;
  _authInterceptorInstalled = true;
  axios.interceptors.response.use(
    (resp) => resp,
    async (err) => {
      const status = err?.response?.status;
      const url = err?.config?.url || '';
      const isOwnApi = url.includes('/api/');
      const isAuthRoute = url.includes('/api/auth/me')
        || url.includes('/api/auth/login')
        || url.includes('/api/auth/refresh')
        || url.includes('/api/auth/logout');
      const alreadyRetried = err?.config?._mpRetried;

      if (status === 401 && isOwnApi && !isAuthRoute && !alreadyRetried) {
        try {
          // Iter 69 — race fix. The previous logic set _refreshPromise = null
          // in `.finally()` immediately when /auth/refresh resolved. A second
          // 401 arriving 1-50ms later (concurrent request that was already
          // in-flight when the access_token expired) would then trigger a
          // SECOND /auth/refresh call with the SAME refresh_token JTI — the
          // backend correctly flags this as a reuse-attack and wipes the
          // user's session. The result: user gets logged out mid-generation.
          //
          // The new logic:
          //   1. Keep _refreshPromise alive for an extra 500ms after success
          //      so all concurrent 401s share the same refresh.
          //   2. Track _lastRefreshAt and short-circuit any 401 that arrives
          //      within 1s of a successful refresh — they are guaranteed to
          //      be in-flight requests that just hadn't seen the new cookies.
          const sinceLast = Date.now() - _lastRefreshAt;
          if (sinceLast < 1000) {
            // Wait 100ms for the cookies to be picked up, then replay without re-refreshing.
            await new Promise((r) => setTimeout(r, 100));
          } else if (!_refreshPromise) {
            _refreshPromise = (async () => {
              await axios.post(
                `${API_URL}/api/auth/refresh`,
                {},
                { withCredentials: true, timeout: 10000 },
              );
              _lastRefreshAt = Date.now();
              // Linger long enough for ALL concurrent 401s (typically <50ms apart)
              // to await this same promise instead of starting a new refresh.
              await new Promise((r) => setTimeout(r, 500));
            })();
            _refreshPromise.finally(() => { _refreshPromise = null; });
          }
          if (_refreshPromise) await _refreshPromise;
          // Critical: give the browser a tick to commit the Set-Cookie header
          // from the refresh response before replaying the original request.
          // Without this, Cloudflare/Brave can race and resend the OLD cookie.
          await new Promise((r) => setTimeout(r, 80));
          // Refresh worked → replay the original request. We rebuild a fresh
          // axios call instead of cloning err.config so the browser's cookie jar
          // is consulted again (some browsers cache the request snapshot).
          const orig = err.config || {};
          return axios({
            url: orig.url,
            method: orig.method || 'get',
            data: orig.data,
            params: orig.params,
            headers: { ...orig.headers },
            withCredentials: true,
            timeout: orig.timeout || 15000,
            _mpRetried: true,
          });
        } catch (refreshErr) {
          // Refresh itself failed → genuine session expiry. Notify the UI.
          // We swallow any error from the callback (rare event listener failure)
          // because we don't want to mask the original 401 the caller will receive.
          try { onUnauthorized?.(); } catch (cbErr) {
            if (typeof console !== 'undefined') logger.warn('[Auth] onUnauthorized callback threw:', cbErr?.message);
          }
          return Promise.reject(err);
        }
      }
      return Promise.reject(err);
    },
  );
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (err) {
      logger.error("Auth check failed:", err?.message);
      // Embarqué (side panel de l'extension / iframe Doctolib) sans session
      // dans cette jarre de cookies : tenter le relais de session de
      // l'extension AVANT d'afficher l'écran de connexion. Une connexion
      // faite n'importe où vaut alors partout.
      if (err?.response?.status === 401 && typeof window !== 'undefined' && window.self !== window.top) {
        const bootstrapped = await tryExtensionBootstrap();
        if (bootstrapped) {
          try {
            const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
            setUser(data);
            setLoading(false);
            return;
          } catch (retryErr) {
            logger.error("Auth check after bootstrap failed:", retryErr?.message);
          }
        }
      }
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Install the global 401-handler once. Rather than force-redirect, we flip a
    // flag via window.dispatchEvent so UIs can show a dismissible "Session expirée"
    // banner with a manual "Se reconnecter" button. Much less aggressive than
    // replacing the URL.
    installAuthInterceptor(() => {
      const path = window.location.pathname || '';
      // Skip public / auth pages
      if (path.startsWith('/login') || path.startsWith('/bilan/')) return;
      // Dispatch once per "session expired" batch (UI components decide what to show)
      window.dispatchEvent(new CustomEvent('session:expired'));
    });
    checkAuth();

    // Iter 70.5 — Iframe heartbeat. Inside Doctolib's iframe, Chrome's CHIPS
    // policy can evict our Partitioned cookies more aggressively than on the
    // top-level site (e.g. after Doctolib navigates between agendas). Without
    // this check, the doctor could dictate for 5 minutes, click "Générer"
    // and only THEN discover their session has expired — losing the work.
    //
    // We ping ``/auth/me`` every 60s when running inside an iframe. A 401
    // surfaces immediately as a "Session expirée" banner so the doctor can
    // re-login BEFORE starting their dictation.
    const isInIframe = typeof window !== 'undefined' && window.self !== window.top;
    if (!isInIframe) return undefined;
    const heartbeat = setInterval(async () => {
      try {
        await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      } catch (err) {
        if (err?.response?.status === 401) {
          // The axios interceptor already retried once via /auth/refresh. If
          // we still get 401 here, the session is genuinely dead → dispatch.
          window.dispatchEvent(new CustomEvent('session:expired'));
        }
      }
    }, 60000);
    return () => clearInterval(heartbeat);
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      // 2FA email : pas de cookie de session encore posé — un code a été
      // envoyé par email, le client doit poster /auth/login/otp pour finir.
      if (data && data.otp_required) {
        return {
          success: true,
          otpRequired: true,
          challengeToken: data.challenge_token,
          email: data.email,
        };
      }
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const loginOtp = useCallback(async (challengeToken, code) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/login/otp`,
        { challenge_token: challengeToken, code },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const resendOtp = useCallback(async (challengeToken) => {
    try {
      await axios.post(
        `${API_URL}/api/auth/login/otp/resend`,
        { challenge_token: challengeToken },
        { withCredentials: true }
      );
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      logger.error("Logout request failed:", err?.message);
    }
    setUser(false);
  }, []);

  const isSessionValid = useCallback(async () => {
    // Iter 75 (2026-06-07) — Lightweight "is my session still alive?" probe.
    // Used by useAudioRecording before starting a recording or sending audio.
    //
    // CRITICAL: we MUST NOT call setUser(false) here. Doing so would trigger
    // a hard <Navigate to="/login"> in ProtectedRoute (App.js:35-41) — i.e. a
    // brutal redirect that destroys the dictation the doctor just performed.
    // Instead we just return true/false and let the caller decide. The axios
    // interceptor will already have dispatched `session:expired`, which
    // SessionExpiredBanner picks up to show a non-blocking "Se reconnecter"
    // banner. The doctor can then re-login in a new tab without losing the
    // iframe state, and the Iter 70.8 auto-resume restores their work.
    try {
      await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      return true;
    } catch {
      return false;
    }
  }, []);

  const contextValue = useMemo(
    () => ({ user, loading, login, loginOtp, resendOtp, logout, checkAuth, isSessionValid }),
    [user, loading, login, loginOtp, resendOtp, logout, checkAuth, isSessionValid]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
